import { setUser } from '@sentry/react'
import { useMemo, useRef } from 'preact/compat'
import { useCallback, useEffect, useState } from 'react'
import { FullScreen, useFullScreenHandle } from 'react-full-screen'
import { get_loading_screen } from '../api/screen'
import { getTranslations } from '../api/translations'
import MercureLayout from '../components/Layout/MercureLayout'
import LoadingScreen from '../components/Screen/LoadingScreen'
import OfflineManager from '../components/Screen/Utils/OfflineManager'
import { root_rotate, screenToken, sentry_key, tokenId } from '../config/constant'
import { AppContextProvider } from '../context/AppContext'
import { ServiceWorkerProvider } from '../context/ServiceWorkerContext'
import InitContentScreen from '../screenContent/InitContentScreen'
import InitPlaylistContentScreen from '../screenContent/InitPlaylistContentScreen'
import { sendDataToApp } from '../utils/android'
import { isOdd } from '../utils/misc'
import { getItem, getSessionKey, setItem } from '../utils/storage'
import { trans } from '../utils/utilsTrans'

const Home = () => {
  const handle = useFullScreenHandle()

  const uriParams = useMemo(
    () =>
      location.search.length
        ? location.search.replace('?', '').split('&')
        : null,
    [],
  )
  const isDemo = useMemo(() => location.pathname === '/demo', [])
  const localParams = useMemo(
    () =>
      uriParams?.reduce((params, param) => {
        const [key, val] = param.split('=')
        params[key] = val
        return params
      }, {}) ?? {},
    [uriParams],
  )

  const getUniqId = useCallback(() => {
    const timestamp = Date.now()
    const token = timestamp.toString().slice(3)
    setItem(tokenId, token)
    return token
  }, [])

  if ('preinitialize' in localParams) {
    const sessionKey = getSessionKey('')

    if (sessionKey) {
      setItem(screenToken, sessionKey)
      setItem(tokenId, localParams.id)
    }
  }

  if ('clear' in localParams) {
    setItem(screenToken, null)
  }

  const [id] = useState(getItem(tokenId) ?? getUniqId())
  const [token] = useState(getItem(screenToken))
  const [currentAppId] = useState(0)
  const [params] = useState(localParams)
  const [displayFullScreen, setDisplayFullScreen] = useState(!token && !params?.preview_flow)
  const [translations, setTranslations] = useState({})
  const [isApp] = useState('isApp' in localParams)
  const [isLoadingScreen] = useState('backgroundLinkApi' in localParams)
  const [backgroundLinkApi] = useState(localParams?.backgroundLinkApi)
  const [loadingScreen, setLoadingScreen] = useState(null)

  const container = useRef()

  const reloadApp = (isHard, onlyApp) => {
    sendDataToApp(
      'log',
      JSON.stringify({
        tokenId: getItem(tokenId),
        screenToken: getItem(screenToken),
        localStorageAccess: !!localStorage,
      }),
    )

    if (isApp) {
      setTimeout(() => {
        sendDataToApp(isHard ? 'delete_all_data' : 'reload')
      }, 1000)
    } else if (!onlyApp) {
      const sessionKey = getSessionKey('')
      window.location.href = `${window.location.href.split('?')[0]}?${sessionKey ? `session_key=${sessionKey}&` : ''}nocache=${Date.now()}`
    }
  }

  useEffect(() => {
    if (backgroundLinkApi) {
      get_loading_screen(`/${backgroundLinkApi}`)
        .then((response) => setLoadingScreen(response))
        .catch((error) => console.error(error))
    }
  }, [backgroundLinkApi])

  useEffect(() => {
    if ('reset' in localParams) {
      setItem(tokenId, getUniqId())
    }

    getTranslations(navigator.language)
      .then((response) => setTranslations(response.translations))
      .catch((error) => console.error(error))
  }, [])

  const rotate = getItem(root_rotate) ?? 0
  const shouldReverse = isOdd(rotate / 90)

  useEffect(() => {
    if (container.current) {
      container.current.style.transform = `rotate(${rotate}deg)`

      if (shouldReverse) {
        container.current.classList.add(`rotate_${rotate}`)
      }
    }
  }, [])

  useEffect(() => {
    if ((id || token) && sentry_key) {
      setUser({ id, username: token })
    }
  }, [id, token])

  const subscribeTopics = useMemo(
    () => ({ screen_id: id ? [id] : [], screen_token: token ? [token] : [] }),
    [id, token],
  )

  if ('clear' in localParams) {
    reloadApp()
  }

  if (backgroundLinkApi) {
    return <LoadingScreen loadingScreen={loadingScreen} fade={true} />
  }

  return (
    <ServiceWorkerProvider>
      <AppContextProvider>
        {params?.preview_flow ? (
          <div id={'preview_mode'}>
            <p>Preview</p>
          </div>
        ) : null}
        <div style={{ width: '100%', height: '100%' }}>
          <MercureLayout
            id={id}
            token={token}
            subscribeTopics={subscribeTopics}
            reloadApp={reloadApp}
          >
            {!token && !params?.preview_flow && !isDemo ? (
              <InitContentScreen
                isApp={isApp}
                isDemo={isDemo}
                translations={translations}
                previewConfig={params}
              />
            ) : (
              <FullScreen handle={handle}>
                <div
                  id='tv-container'
                  role='application'
                  ref={container}
                  onClick={handle.active ? handle.exit : handle.enter}
                  onKeyDown={() => {}}
                >
                  {!handle.active &&
                  displayFullScreen &&
                  !params?.preview_flow &&
                  !isApp ? (
                    <p
                      className='fullscreen-button'
                      onAnimationEnd={() => setDisplayFullScreen(false)}
                      style={{ opacity: displayFullScreen ? 1 : 0 }}
                    >
                      {trans('full_screen', translations)}
                    </p>
                  ) : null}
                  <InitPlaylistContentScreen
                    isDemo={isDemo}
                    loadingScreen={loadingScreen}
                    isLoadingScreen={isLoadingScreen}
                    isApp={isApp}
                    translations={translations}
                    isReverse={shouldReverse}
                    appid={currentAppId}
                    preview_data={params}
                    reloadApp={reloadApp}
                  />
                </div>
              </FullScreen>
            )}
          </MercureLayout>
        </div>
        <OfflineManager translations={translations} />
      </AppContextProvider>
    </ServiceWorkerProvider>
  )
}

export default Home
